/* eslint-disable camelcase */
import React, { useMemo, useState } from 'react';
import LegacyPratusTable from '../../../DesignSystems/PratusTable/LegacyPratusTable';
import { StylishNewButton } from '../../../DesignSystems/New/StylishNewButton';
import { Badge, Form } from 'react-bootstrap';
import Loading from '../../../Loading/Loading';
import {
  ResourceInIncident,
  useMarkArrived,
  useUpdateDemob,
} from '../../hooks/resourceInIncidentHooks';
import { FaEdit } from 'react-icons/fa';
import CheckOutDrawer from '../Drawers/CheckOutDrawer';
import { STATUS_CONFIG } from '../constants/resourceConstants';
import { getStatusColor } from '../utils/statusUtils';
import { formatDateTime } from '../utils/dateUtils';
import StylishNewSelect from '../../../DesignSystems/New/StylishNewSelect';

import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import Sentry from '../../../../instrument';

interface CheckOutTableProps {
  resources: ResourceInIncident[];
  isLoading?: boolean;
}

const CheckOutTable: React.FC<CheckOutTableProps> = ({
  resources,
  isLoading,
}) => {
  const { exportAsForm } = useIcsFormPdfPreview();
  const [showCheckedOutOnly, setShowCheckedOutOnly] = useState(true);
  const [showCheckOutDrawer, setShowCheckOutDrawer] = useState(false);
  const [selectedResource, setSelectedResource] =
    useState<ResourceInIncident | null>(null);
  const [selectedKind, setSelectedKind] = useState<string>('all');
  const [searchText, setSearchText] = useState<string>('');

  // Post-checkout edits go through the demob endpoint rather than the initial check-out: it writes only the fields
  // sent, so a later Block 6 sign-off cannot blank what the Demobilization Unit Leader entered first.
  const updateDemobMutation = useUpdateDemob();
  const markArrivedMutation = useMarkArrived();

  // Get unique kinds and filter resources
  const { availableKinds, filteredResources } = useMemo(() => {
    const kinds = new Set<string>();
    let filtered = resources;

    // Collect kinds from all resources
    resources.forEach((resource) => {
      if (resource.resource_kind) kinds.add(resource.resource_kind);
    });

    if (showCheckedOutOnly) {
      // Show only fully checked out resources
      filtered = filtered.filter(
        (resource) => resource.current_status === 'checked_out'
      );
    }

    // Filter by kind
    if (selectedKind && selectedKind !== 'all') {
      filtered = filtered.filter((r) => r.resource_kind === selectedKind);
    }

    // Filter by search
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase().trim();
      filtered = filtered.filter((resource) => {
        const searchableText = [
          resource.resource_name || '',
          resource.resource_type || '',
          resource.resource_kind || '',
          resource.leader_name || '',
          resource.checkout_destination || '',
          resource.checkout_travel_method || '',
          resource.incident_lodging_info || '',
        ]
          .join(' ')
          .toLowerCase();
        return searchableText.includes(searchLower);
      });
    }

    return {
      availableKinds: Array.from(kinds).sort(),
      filteredResources: filtered,
    };
  }, [resources, showCheckedOutOnly, selectedKind, searchText]);

  if (isLoading) {
    return <Loading />;
  }

  const getReleaseMethodBadge = (method?: string) => {
    const methodConfig: Record<string, { color: string; label: string }> = {
      demobilized: { color: 'primary', label: 'Demobilized' },
      reassigned: { color: 'info', label: 'Reassigned' },
      returned_to_pool: { color: 'success', label: 'Returned to Pool' },
    };

    const config = methodConfig[method || ''] || {
      color: 'secondary',
      label: method || 'Unknown',
    };
    return <Badge bg={config.color}>{config.label}</Badge>;
  };

  const getStatusBadge = (resource: ResourceInIncident) => {
    const status = resource.current_status;
    const config = STATUS_CONFIG[status];

    if (status === 'in_process') {
      return (
        <Badge bg={getStatusColor(status)}>{config?.label || status}</Badge>
      );
    }
    if (status === 'checked_out') {
      return (
        <Badge bg={getStatusColor(status)}>{config?.label || status}</Badge>
      );
    }
    // Default case for any other status
    const color = config ? getStatusColor(status) : 'secondary';
    const label = config ? config.label : status;
    return <Badge bg={color}>{label}</Badge>;
  };

  // Handler functions for form exports
  const handleExportICS221 = (
    resource: ResourceInIncident,
    format: 'pdf' | 'docx' = 'pdf'
  ) => {
    void exportAsForm({
      formname: 'ICS_221_CG',
      data: { resourceId: resource.resource_in_incident_id },
      mapSketch: null,
      format,
    });
  };

  const handleExportICS238 = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: 'ICS_238_CG',
      data: {},
      mapSketch: null,
      format,
    });
  };

  const columns = [
    {
      dataField: 'resource_name',
      text: 'Resource',
      sort: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <div>
          <strong>{row.resource_name || 'Unknown'}</strong>
          <br />
          <small className="text-muted">
            {row.resource_type} • {row.leader_name || 'No leader'}
          </small>
        </div>
      ),
    },
    {
      dataField: 'current_status',
      text: 'Status',
      sort: true,
      formatter: (cell: string, row: ResourceInIncident) => getStatusBadge(row),
    },
    {
      dataField: 'release_method',
      text: 'Release Method',
      sort: true,
      formatter: (cell: string) => getReleaseMethodBadge(cell),
    },
    {
      dataField: 'checkout_datetime',
      text: 'Check-Out Time',
      sort: true,
      formatter: (cell: string) => formatDateTime(cell),
    },
    {
      dataField: 'checkout_destination',
      text: 'Destination',
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'checkout_eta_home',
      text: 'ETA Home',
      formatter: (cell: string) => formatDateTime(cell),
    },
    {
      dataField: 'checkout_travel_method',
      text: 'Travel Method',
      formatter: (cell: string) => cell || '-',
    },
    {
      dataField: 'is_excess',
      text: 'Excess',
      // ICS 238 column 16. Three states, because a resource nobody has reviewed is not one found not to be excess.
      formatter: (cell: boolean | undefined) =>
        cell === undefined || cell === null ? '-' : cell ? 'Yes' : 'No',
    },
    {
      dataField: 'actual_arrival_datetime',
      text: 'Arrived Home',
      // ICS 238 column 20, and the last thing the Demobilization Unit tracks.
      formatter: (cell: string, row: ResourceInIncident) =>
        cell ? (
          formatDateTime(cell)
        ) : (
          <StylishNewButton
            className="button button--link"
            disabled={
              row.current_status !== 'checked_out' ||
              markArrivedMutation.isPending
            }
            onClick={() =>
              markArrivedMutation.mutate({
                id: row.id,
                actualArrivalDatetime: new Date().toISOString(),
              })
            }
            title={
              row.current_status === 'checked_out'
                ? 'Record that this resource reached its home unit'
                : 'Only a released resource can be marked as arrived'
            }
          >
            Mark arrived
          </StylishNewButton>
        ),
    },
    {
      dataField: 'ics221',
      text: 'ICS 221',
      isDummyField: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <FormExportButton
          formname="ICS_221_CG"
          onExport={(format) => handleExportICS221(row, format)}
          label="Preview ICS 221"
        />
      ),
    },
    {
      dataField: 'actions',
      text: 'Actions',
      isDummyField: true,
      formatter: (cell: string, row: ResourceInIncident) => (
        <StylishNewButton
          className="button button--link"
          onClick={() => {
            setSelectedResource(row);
            setShowCheckOutDrawer(true);
          }}
          title="Edit check-out"
        >
          <FaEdit size={16} />
        </StylishNewButton>
      ),
    },
  ];

  const kindOptions = [
    { value: 'all', label: 'All Kinds' },
    ...availableKinds.map((kind) => ({ value: kind, label: kind })),
  ];

  return (
    <div className="check-out-table">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5>ICS 238 Demobilization Check-Out List</h5>
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted">
            {showCheckedOutOnly ? 'Checked Out' : 'All'}
          </span>
          <Form.Switch
            id="checkout-status-toggle"
            checked={showCheckedOutOnly}
            onChange={(e) => setShowCheckedOutOnly(e.target.checked)}
          />
        </div>
      </div>

      {/* ICS 238 Export Button */}
      <div className="mb-3">
        <FormExportButton
          formname="ICS_238_CG"
          onExport={handleExportICS238}
          label="Preview ICS 238"
        />
      </div>

      <div className="d-flex gap-3 mb-3">
        <div style={{ width: '250px' }}>
          <StylishNewSelect
            options={kindOptions}
            value={kindOptions.find((opt) => opt.value === selectedKind)}
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedKind(option?.value || 'all');
            }}
            placeholder="Filter by Kind"
          />
        </div>
        <div className="flex-fill">
          <Form.Control
            type="text"
            placeholder="Search resources..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
      </div>

      <LegacyPratusTable
        keyField="id"
        columns={columns}
        rows={filteredResources}
      />

      {/* Check-Out Drawer for editing */}
      {showCheckOutDrawer && selectedResource && (
        <CheckOutDrawer
          resource={selectedResource}
          isUpdate={true}
          onClose={() => {
            setShowCheckOutDrawer(false);
            setSelectedResource(null);
          }}
          onSave={async (data: Partial<ResourceInIncident>) => {
            // Fix: Actually save the checkout data to the server
            try {
              if (selectedResource?.id) {
                await updateDemobMutation.mutateAsync({
                  id: selectedResource.id,
                  data,
                });
              }
            } catch (error) {
              Sentry.captureException(error);
              // Don't close drawer on error so user can try again
              return;
            }

            // Only close drawer after successful save
            setShowCheckOutDrawer(false);
            setSelectedResource(null);
          }}
        />
      )}
    </div>
  );
};

export default CheckOutTable;
